/**
 * API Types
 *
 * Re-exports from auto-generated OpenAPI types (source of truth: swagger.json).
 * Run `npm run generate:api-types` after any API change to refresh.
 *
 * Friendly aliases keep consumer imports simple.
 * Only types NOT derivable from swagger (e.g. ApiClientError) are hand-written here.
 */
import type { components } from './generated/api-types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Helper – extract a schema type by name */
type Schema<T extends keyof components['schemas']> = components['schemas'][T];

// ─── Auth ────────────────────────────────────────────────────────────────────

/** Impersonation metadata returned by /auth/me when impersonating */
export interface ImpersonationInfo {
  active: boolean;
  sessionId: string;
  actor: {
    id: string;
    displayName: string;
  };
  expiresAt?: string;
}

/** User profile — extends generated type with OAuth fields (available after swagger regen) */
export type User = Schema<'UserProfileResponse'> & {
  avatarUrl?: string | null;
  authProvider?: string;
  emailVerified?: boolean;
  role?: string;
  permissions?: string[];
  isActive?: boolean;
  impersonation?: ImpersonationInfo;
};
export type LoginRequest = Schema<'LoginBody'>;
export type RegisterRequest = Schema<'RegisterBody'>;
export type AuthTokens = Schema<'AuthTokens'>;
export type RefreshTokenResponse = Schema<'RefreshTokenResponse'>;
export type RefreshTokenRequest = Schema<'RefreshTokenBody'>;
export type MessageResponse = Schema<'MessageResponse'>;

// ─── Connectors ──────────────────────────────────────────────────────────────

export type AuthMethod = Schema<'AuthMethod'>;
export type ConnectorCategory = Schema<'ConnectorCategory'>;
export type ConnectorSummary = Schema<'ConnectorSummary'>;
/** Full connector detail (with typed AuthMethod enum) */
export type Connector = Schema<'ConnectorData'>;

// ─── Connections ─────────────────────────────────────────────────────────────

/** Connection as returned in list endpoints */
export type ConnectionListItem = Schema<'ConnectionListItem'>;
/** Connection detail (with hasCredentials, credentialFields) */
export type Connection = Schema<'ConnectionResponse'>;
export type ConnectionListResponse = Schema<'ConnectionListResponse'>;
export type CreateConnectionRequest = Schema<'CreateConnectionBody'>;
export type UpdateConnectionRequest = Schema<'UpdateConnectionBody'>;
export type ConnectionTestResult = Schema<'ConnectionTestResult'>;

// ─── OAuth ───────────────────────────────────────────────────────────────────

export type OAuthInitiateResponse = Schema<'OAuthInitiateResponse'>;
export type OAuthCallbackResponse = Schema<'OAuthCallbackResponse'>;
export type AdminConsentInitiateResponse = Schema<'AdminConsentInitiateResponse'>;

// ─── Entities ────────────────────────────────────────────────────────────────

export type EntityDiscoveryItem = Schema<'EntityDiscoveryItem'>;
export type EntityPreset = Schema<'EntityPreset'>;
export type EntityGroupKey = Schema<'EntityGroupKey'>;
export type EntityGroup = Schema<'EntityGroup'>;
export type EntityRelationship = Schema<'EntityRelationship'>;
export type EntitySchemaInspection = Schema<'EntitySchemaInspection'>;
export type EntityPreviewInspection = Schema<'EntityPreviewInspection'>;
export type EntityListResponse = Schema<'EntityListResponse'>;
export type SelectedEntity = Schema<'SelectedEntity'>;
export type SaveEntitiesBody = Schema<'SaveEntitiesBody'>;
export type PostgreSQLCDCConfig = Schema<'PostgreSQLCDCConfig'>;
export type PostgreSQLCDCDiscovery = Schema<'PostgreSQLCDCDiscovery'>;

// ─── Entity Discovery (Async) ────────────────────────────────────────────────

export type EntityDiscoveryStatus = Schema<'EntityDiscoveryStatus'>;
export type EntityDiscoveryStatusResponse = Schema<'EntityDiscoveryStatusResponse'>;
export type DiscoverEntitiesResponse = Schema<'DiscoverEntitiesResponse'>;

// ─── File upload ─────────────────────────────────────────────────────────────

export type FileUploadResponse = Schema<'FileUploadResponse'>;

// ─── Ingestion status ────────────────────────────────────────────────────────

export type IngestionStatusResponse = Schema<'IngestionStatusResponse'>;
export type IngestionDatasetProgress = Schema<'IngestionDatasetProgress'>;
export type IngestionStage = Schema<'IngestionStage'>;
export type DatasetStage = Schema<'DatasetStage'>;
export type IngestionStatus = Schema<'ConnectionStatus'>;

// ─── Chart types ─────────────────────────────────────────────────────────────

export type ChartType = Schema<'ChartType'>;
export type ChartPositionConfig = Schema<'ChartPositionConfig'>;
export type ChartStyle = Schema<'ChartStyle'>;
export type ChartFieldsConfig = Schema<'ChartFieldsConfig'>;
export type ChartStyleConfig = Schema<'ChartStyleConfig'>;
export type FieldMapping = Schema<'ChartFieldMapping'>;
export type FieldMappingMap = Schema<'FieldMappingMap'>;
export type Chart = Schema<'ChartResponse'>;

/** Chart response enriched with dataset view columns for field mapping editor */
export interface ChartWithColumns extends Chart {
  datasetViewColumns?: DatasetViewColumn[];
}

// ─── Dataset view column metadata ────────────────────────────────────────────

export interface DatasetViewColumn {
  name: string;
  /** Physical type: "string" | "integer" | "double" | "date" | "datetime" | "boolean" */
  type: string;
  /** Semantic type: "measure" | "dimension" | "time" | "id" */
  semantic_type: string;
  /** Default aggregation: "sum" | "avg" | "count" | "count_distinct" | "min" | "max" | "none" | null */
  default_aggregation: string | null;
  display_name?: string;
  /** Original source table/entity name (e.g. "Opportunity", "Account") for semantic grouping */
  source_entity?: string;
  /** Entity classification: "grain_fact" | "fact" | "dimension" — controls display order in field picker */
  entity_type?: string;
  /** Whether this column is a calculated/derived field */
  is_calculated?: boolean;
  /** SQL expression / formula for calculated fields */
  formula?: string;
  /** Plain-English description of the formula */
  formula_description?: string;
  /** Distinct value count for dimensions/dates (cardinality) */
  distinct_count?: number;
  /** Origin kind for projected fields; currently "json" for promoted JSON paths */
  source_kind?: 'json';
  /** Dataset containing the original JSON document column */
  source_dataset_id?: string;
  /** Original JSON document column */
  source_column?: string;
  /** Canonical JSON path within the source document */
  source_path?: string;
}

// ─── Chart suggestion types ──────────────────────────────────────────────────

export type NewChartSuggestionItem = Schema<'NewChartSuggestionItem'>;
export type EditChartSuggestionItem = Schema<'EditChartSuggestionItem'>;
export type ChartSuggestionsData = Schema<'ChartSuggestionsData'>;

// ─── Dashboard types ─────────────────────────────────────────────────────────

export type DashboardFilter = Schema<'DashboardFilterResponse'>;
export type DashboardChartSummary = Schema<'DashboardChartSummary'>;
export type DashboardFilterSummary = Schema<'DashboardFilterSummary'>;
export type DashboardListItem = Schema<'DashboardListItem'>;
export type DashboardListResponse = Schema<'DashboardListResponse'>;
export type Dashboard = Schema<'DashboardDetailResponse'>;
export type CreateDashboardRequest = Schema<'CreateDashboardBody'>;
export type CreateDashboardResponse = Schema<'CreateDashboardResponse'>;
export type UpdateDashboardRequest = Schema<'UpdateDashboardBody'>;
export type DashboardSettings = Schema<'Partial_DashboardSettings_'>;
export type DashboardLayoutSettings = Schema<'DashboardLayoutSettings'>;
export type UpdateDashboardSettingsRequest = Schema<'UpdateDashboardSettingsBody'>;
export type CreateChartRequest = Schema<'ChartCreateBody'>;
export type UpdateChartRequest = Schema<'ChartUpdateBody'>;
/**
 * A chart that has been validated and is ready to render.
 * Emitted progressively via CHART_READY events during streaming compilation.
 */
export interface ReadyChart {
  index: number;
  chartId: string;
  type: string;
  title: string;
}

/**
 * A filter that has been validated and is ready to render.
 * Emitted progressively via FILTER_READY events during streaming compilation.
 */
export interface ReadyFilter {
  index: number;
  name: string;
  type: string;
}

/**
 * Extended DashboardCreationStatus with progressive streaming fields.
 * The base type comes from swagger; readyCharts/readyFilters are added
 * by the API before swagger is regenerated.
 */
export type DashboardCreationStatus = Schema<'DashboardCreationStatus'> & {
  readyCharts?: ReadyChart[];
  readyFilters?: ReadyFilter[];
};
export type PlanPreview = Schema<'PlanPreview'>;
export type PlanPreviewChart = Schema<'PlanPreviewChart'>;
export type PlanPreviewFilter = Schema<'PlanPreviewFilter'>;
export type ChartLayoutItem = Schema<'ChartLayoutItem'>;
export type UpdateChartsLayoutBody = Schema<'UpdateChartsLayoutBody'>;
export type UpdateChartsLayoutResponse = Schema<'UpdateChartsLayoutResponse'>;
export type ResetChartsLayoutResponse = Schema<'ResetChartsLayoutResponse'>;

/** Inline step type extracted from DashboardCreationStatus */
export type CreationStep = DashboardCreationStatus['steps'][number];

// ─── Query data body ─────────────────────────────────────────────────────────

export type QueryDataBody = Schema<'ChartQueryBody'>;
/** Filter values: string, string[], boolean, or nested objects for date ranges */
export type FilterValue = string | string[] | boolean | Record<string, string | number>;

// ─── Filter data ─────────────────────────────────────────────────────────────

export type CategoricalFilterStats = Schema<'CategoricalFilterStats'>;
export type DateRangeFilterStats = Schema<'DateRangeFilterStats'>;
export type NumericFilterStats = Schema<'NumericFilterStats'>;
export type BooleanFilterStats = Schema<'BooleanFilterStats'>;
export type FilterStats = Schema<'FilterStats'>;
export type FilterOption = Schema<'FilterOptionData'>;
export type FilterDataResponse = Schema<'FilterDataResponse'>;

// ── Filter UI type — derived client-side from type + stats ───────────────────

export type FilterUiType = 'checkbox_list' | 'searchable_list' | 'search_select';

const CHECKBOX_LIST_MAX = 200;
const SEARCHABLE_LIST_MAX = 5000;

/**
 * Derive filter UI type from filter type and stats.
 * Only categorical filters have UI type differentiation.
 */
export function deriveFilterUiType(
  filterType: string,
  stats: FilterStats | null,
): FilterUiType {
  if (filterType !== 'categorical') return 'checkbox_list';
  const dc = (stats as CategoricalFilterStats | null)?.distinct_count ?? 0;
  if (dc <= CHECKBOX_LIST_MAX) return 'checkbox_list';
  if (dc <= SEARCHABLE_LIST_MAX) return 'searchable_list';
  return 'search_select';
}

// ─── Unified chart data (from query service via proxy) ───────────────────────

/** Unified tabular chart data — all chart types use this single shape. */
export interface ChartColumn {
  name: string;
  type: string; // "string" | "number" | "date"
}

export interface ChartMeta {
  x?: string;
  y?: string[];
  series?: string;
  /** Bubble size column for scatter charts */
  size?: string;
  /** Human-readable labels (from fieldMappings) */
  xLabel?: string;
  yLabels?: string[];
  seriesLabel?: string;
}

export interface ChartData {
  chartType: string;
  columns: ChartColumn[];
  rows: any[][];
  meta?: ChartMeta | null;
  /** KPI sparkline trend data — array of {date, value} points, sorted ascending */
  trend?: TrendDataPoint[] | null;
}

export type ChartQueryMetadata = Schema<'ChartQueryMetadata'>;

/** KPI sparkline trend data point */
export interface TrendDataPoint {
  date: string;
  value: number;
}

export interface ChartDataResponse {
  chart_id: string;
  chart_type: string;
  dataset_id: string;
  data: ChartData;
  metadata: ChartQueryMetadata;
}

// ─── Data Models ─────────────────────────────────────────────────────────────

export type DataModelStatus = Schema<'DataModelStatus'>;
export type DataModelListItem = Schema<'DataModelListItem'>;
export type DataModelListResponse = Schema<'DataModelListResponse'>;
export type DataModelDetail = Schema<'DataModelDetail'>;
export type CreateDataModelBody = Schema<'CreateDataModelBody'>;
export type UpdateDataModelBody = Schema<'UpdateDataModelBody'>;
export type BuildDataModelResponse = Schema<'BuildDataModelResponse'>;
export type BuildDataModelBody = Schema<'BuildDataModelBody'>;
export type BuildStatus = Schema<'BuildStatus'>;

// ─── Sharing ─────────────────────────────────────────────────────────────────

export type ShareInfoResponse = Schema<'ShareInfoResponse'>;
export type ShareUpdateResponse = Schema<'ShareUpdateResponse'>;
export type ShareDashboardBody = Schema<'ShareDashboardBody'>;
export type PublicDashboard = Schema<'PublicDashboardResponse'>;
export type SharedDashboardListItem = Schema<'SharedDashboardListItem'>;
export type SharedDashboardListResponse = Schema<'SharedDashboardListResponse'>;
export type AcceptDashboardShareResponse = Schema<'AcceptDashboardShareResponse'>;

// ─── Pagination (generic, not in swagger) ────────────────────────────────────

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ─── Generic API helpers (not in swagger) ────────────────────────────────────

export interface ApiError {
  message: string;
  statusCode: number;
  details?: unknown;
  errorId?: string;
  correlationId?: string;
}

// ─── Error class (runtime, not in swagger) ───────────────────────────────────

export class ApiClientError extends Error {
  constructor(
    public statusCode: number,
    public error: string,
    public details?: unknown,
    public errorId?: string,
    public correlationId?: string,
  ) {
    super(error);
    this.name = 'ApiClientError';
  }
}
